import { Component, Input, OnInit } from '@angular/core';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { AppLocalizationService } from '../../../../services/AppLocalizationService';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { Router } from '@angular/router';
import { EventBusService } from '../../../../services/event-bus.service';
import { ReportDesignerTemplatePersistanceService } from '../../services/report-designer-template-persistance.service';
import { ReportDesignerService } from '../../services/report-designer.service';
import { AppLoadingIndicatorService } from '../../../../services/app-loading-indicator.service';
import { AppSettingsService } from '../../../../services/app-settings.service';
import { ReportDesignerStorageService } from '../../services/report-designer-storage.service';
import { AppStorageService } from '../../../../services/app-storage.service';
import { WidgetLockerService } from '../../services/WidgetLockerService';

/**
 * Report designer toolbar
 */
@Component({
  selector: 'report-designer-toolbar',
  templateUrl: './report-designer-toolbar.component.html',
  styleUrls: ['./report-designer-toolbar.component.scss']
})
export class ReportDesignerToolbarComponent extends DestroyBase implements OnInit {
  /**
   * The RD host component
   */
  @Input() main: ReportDesignerHostComponent;

  /**
   * Gets the template mode state
   */
  public get templateMode(): boolean {
    return !this.reportOid;
  }

  /**
   * The mode
   */
  public mode: 'report' | 'template' = "template";

  /**
   * The report OID
   */
  public reportOid: number = null;

  /**
   * Toolbar read-only?
   */
  public toolbarReadOnly = true;

  /**
   * Widget list mode?
   */
  public widgetListMode = true;

  /**
   * Widget info mode?
   */
  public widgetInfoMode = true;

  /**
   * Enable toggling edit mode?
   */
  public enableButtonToggleEditMode = false;

  /**
   * Enable toggling header mode?
   */
  public enableButtonToggleWidgetHeaderMode = false;

  /**
   * Enable show page nav?
   */
  public enableButtonShowPageNavigation = false;

  /**
   * Enable load template?
   */
  public enableButtonLoadDocument = false;

  /**
   * Enable save template?
   */
  public enableButtonSaveDocument = false;

  /**
   * Enable delete template?
   */
  public enableButtonDeleteDocument = false;

  /**
   * Enable translate widgets?
   */
  public enableButtonTranslateWidgets = false;

  /**
   * Enable save report?
   */
  public enableButtonSaveReport = false;

  /**
   * Enable new template?
   */
  public enableButtonNewDocument = false;

  /**
   * Show new template?
   */
  public showButtonNewDocument = false;

  /**
   * Show load template?
   */
  public showButtonLoadDocument = false;

  /**
   * Show save template?
   */
  public showButtonSaveDocument = false;

  /**
   * Show delete template?
   */
  public showButtonDeleteDocument = false;

  /**
   * Show translate widgets?
   */
  public showButtonTranslateWidgets = false;

  /**
   * Show save report?
   */
  public showButtonSaveReport = false;

  /**
   * Selected Dictionary language ID
   */
  public selectedLanguageId = 1;

  /**
   * Available Dictionary languages
   */
  public availableLanguages: any[];

  /**
   * Constructor
   * @param appSettings
   * @param appLoadingIndicator
   * @param rdStorage
   * @param appStorage
   * @param eventBus
   * @param router
   * @param appLocalization
   * @param reportDesignerPersistanceService
   * @param reportDesignerService
   * @param widgetLocker
   */
  constructor(private appSettings: AppSettingsService,
    private appLoadingIndicator: AppLoadingIndicatorService,
    private rdStorage: ReportDesignerStorageService,
    private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private router: Router,
    private appLocalization: AppLocalizationService,
    private reportDesignerPersistanceService: ReportDesignerTemplatePersistanceService,
    private reportDesignerService: ReportDesignerService,
    private widgetLocker: WidgetLockerService) {
    super();
  }

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.setMode(this.mode);

    this.onEvents.push(this.eventBus.subscribeEvent('report.designer.widget.list.visibility', (mode: boolean) => {
      this.widgetListMode = mode;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportOpened', (report) => {
      this.reportOpened(report);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('templateOpened', () => {
      this.toolbarReadOnly = false;
      this.refreshLanguagesDataSource(null);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('createTemplate', () => {
      this.enableButtonToggleEditMode = true;
      this.enableButtonToggleWidgetHeaderMode = true;
      this.enableButtonShowPageNavigation = true;
      this.enableButtonLoadDocument = true;

      this.setEditMode(false);
      this.setGridRasterMode(true);
      this.setHeaderMode(true);

      this.enableButtonSaveDocument = true; //New mode -> enable saving

      if (this.appStorage.userIsInRole(['Admin', 'Project Team'])) {
        this.enableButtonDeleteDocument = true;
      }
      else {
        this.enableButtonDeleteDocument = false;
        this.enableButtonTranslateWidgets = false;
      }
      this.createTemplate();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent("reportDesignerDialog.templateSaved", () => {
      this.refreshLanguagesDataSource(null);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('enableButtonsForLoadDocument', () => {
      this.enableButtonToggleEditMode = true;
      this.enableButtonToggleWidgetHeaderMode = true;
      this.enableButtonShowPageNavigation = true;

      this.setEditMode(false);
      this.setGridRasterMode(false);
      this.setHeaderMode(false);

      this.enableButtonSaveReport = false;

      this.enableButtonSaveDocument = true;

      if (this.appStorage.userIsInRole(['Admin', 'Project Team'])) {
        this.enableButtonDeleteDocument = true;
      }
      else {
        this.enableButtonDeleteDocument = false;
        this.enableButtonTranslateWidgets = false;
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('enableButtonsForLoadReport', () => {
      this.enableButtonToggleEditMode = true;
      this.enableButtonToggleWidgetHeaderMode = true;
      this.enableButtonShowPageNavigation = true;

      this.setEditMode(false);
      this.setGridRasterMode(false);
      this.setHeaderMode(false);

      this.enableButtonLoadDocument = true;
      this.enableButtonSaveReport = true;

      if (this.appStorage.userIsInRole(['Admin', 'Project Team'])) {
        this.enableButtonDeleteDocument = true;
      }
      else {
        this.enableButtonDeleteDocument = false;
        this.enableButtonSaveDocument = false;
        this.enableButtonTranslateWidgets = false;
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('templateLoaded', () => {
      if (this.appStorage.userIsInRole(['Admin'])) {
        this.enableButtonTranslateWidgets = true;
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', () => {
      this.appLoadingIndicator.hide();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('report.designer.widgets.saved', () => {
      this.appLoadingIndicator.hide();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent("newTemplate", () => {
      this.createTemplate();
      this.enableButtonTranslateWidgets = false;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent("rd:dictionaryConfigIdSet", () => {
      //adjust languages dropdown
      const config = this.appLocalization.getReportDesignerDictionaryConfig();
      const languageId = config.id;
      this.refreshLanguagesDataSource(languageId);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('report.designer.widget.locks.changed', () => {
      const widgetsLockedByUser = this.widgetLocker.getLockedWidgetsForUser();
      if (widgetsLockedByUser?.length) {
        this.setEditMode(true);
      }
    }));
  }

  /**
   * Toggle the widget list mode
   */
  public toggleWidgetListMode(): void {
    this.widgetListMode = !this.widgetListMode;
    this.main.setWidgetListVisibility(this.widgetListMode);
  }

  /**
   * Toggle the widget info mode
   */
  public toggleWidgetInfoMode(): void {
    this.widgetInfoMode = !this.widgetInfoMode;
    this.rdStorage.setAppWidgetInfoMode(this.widgetInfoMode);

    this.main.setInfoBarVisibility();
  }

  /**
   * Show the page navigation
   */
  public showPageNavigation(): void {
    this.main.setActiveWidget(null);
  }

  /**
   * Create a new template
   */
  public createTemplate(): void {
    this.rdStorage.setTemplate(null);
    const document = this.main.getActiveDocument();

    this.enableButtonToggleEditMode = true;
    this.enableButtonToggleWidgetHeaderMode = true;
    this.enableButtonShowPageNavigation = true;
    this.enableButtonLoadDocument = true;
    this.enableButtonSaveDocument = true; //Enable the button in the new mode

    this.enableButtonSaveReport = false;

    this.enableButtonDeleteDocument = false;

    if (document?.data?.pages) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          this.setEditMode(false);
          this.setGridRasterMode(true);
          this.setHeaderMode(true);

          this.main.createTemplate();
        }, cancel: () => {
          //Nothing
        }
      }, 'confirmDialog');
    }
    else {
      this.main.createTemplate();
    }
  }

  /**
   * Language change handler
   */
  public languageChanged(): void {
    console.log(`languageChanged: languageId=${this.selectedLanguageId}`);
    //17788: Task 17788: Edition of Report Templates in multiple terminology-languages
    //When I select one of the other available Languages, then PIX will reload the Template to represent the new Language.
    if (this.selectedLanguageId) {
      const languageId = this.selectedLanguageId;
      this.setLanguageAndReloadDocument(languageId);
    }
  }

  /**
   * Set language and reload the current document
   * @param languageId
   */
  public setLanguageAndReloadDocument(languageId: number): void {
    this.appLocalization.setReportDesignerDictionaryConfigById(languageId).then(() => {
      const document = this.main.getActiveDocument();
      const isReportMode = !!this.rdStorage.getReport();
      if (document?.data?.pages?.length) {
        const documentJson = document.data;
        document.clear();
        document.loadDocument(documentJson);
        if (!isReportMode) {
          this.eventBus.broadcastEvent("enableButtonsForLoadDocument");
        }
      }
    });
  }

  /**
   * Refresh the selectable languages
   * @param languageId
   */
  public refreshLanguagesDataSource(languageId: number): void {
    if (languageId === null) {
      const config = this.appLocalization.getReportDesignerDictionaryConfig();
      if (config) {
        languageId = config.id;
      }
    }

    const languages = this.appSettings.availableReportDesignerLocales.map(o => { return { id: o.id, description: o.description }; });
    this.availableLanguages = languages;
    if (languageId && this.selectedLanguageId != languageId) {
      this.selectedLanguageId = languageId;
      this.setLanguageAndReloadDocument(languageId);
    }
  }

  /**
   * Sets the report mode
   */
  private setReportMode(): void {
    //Collapse template

    this.showButtonNewDocument = false;
    this.showButtonLoadDocument = false;
    this.showButtonSaveDocument = false;
    this.showButtonDeleteDocument = false;
    this.showButtonTranslateWidgets = false;

    //Expand report

    this.main.setWidgetListVisibility(false);
    this.showButtonSaveReport = true;
    this.enableButtonSaveReport = true;

    this.enableButtonToggleEditMode = true;
    this.enableButtonToggleWidgetHeaderMode = true;
    this.enableButtonShowPageNavigation = true;

    this.enableButtonNewDocument = false;

    const document = this.main.getActiveDocument();
    if (document?.data?.isReadOnly) {
      this.setToolbarReadOnly();
    }
  }

  /**
   * Sets the template mode
   */
  private setTemplateMode(): void {
    //Expand template

    this.showButtonNewDocument = true;
    this.showButtonLoadDocument = true;
    this.showButtonSaveDocument = true;
    this.showButtonDeleteDocument = true;
    this.showButtonTranslateWidgets = true;

    //Collapse report
    this.showButtonSaveReport = false;

    this.enableButtonToggleEditMode = true;
    this.enableButtonToggleWidgetHeaderMode = true;
    this.enableButtonShowPageNavigation = true;
    this.enableButtonLoadDocument = true;
    this.enableButtonSaveDocument = true;//New mode -> enable saving

    this.setEditMode(false);
    this.setHeaderMode(true);
    this.setGridRasterMode(true);

    if (this.appStorage.userIsInRole(['Admin', 'Project Team'])) {
      this.enableButtonDeleteDocument = true;
    }
    else {
      this.enableButtonDeleteDocument = false;
      this.enableButtonTranslateWidgets = false;
    }

    this.enableButtonNewDocument = true;

    //new template can be done/seen in any supported language (that is active)
    this.appSettings.availableReportDesignerLocales = (this.appSettings.allReportDesignerLocales || []).filter(l => l.isActive);

    this.main.setWidgetListVisibility(true);
    this.main.setEditMode(true);
    this.rdStorage.setAppWidgetHeaderMode(true);
    this.rdStorage.setAppReportGridRasterMode(true);
  }

  /**
   * Sets the mode
   * @param mode
   * @returns
   */
  public setMode(mode: 'report' | 'template'): void {
    if (this.rdStorage.getAppPrintMode()) {
      return;//skip switching mode in print mode
    }
    this.mode = mode;

    this.setEditMode(false);
    this.setHeaderMode(false);
    this.setGridRasterMode(false);

    if (mode === "report") {
      this.setReportMode();
    }
    else {
      this.setTemplateMode();
    }
  }

  /**
   * Print report action
   * */
  public printReport(): void {
    this.eventBus.broadcastEvent("printReport", {
      callback: (settings) => {
        this.appLoadingIndicator.show();
        this.main.isPrintDocumentAction = true;
        this.main.getActiveDocument().exportPDF(settings);
      },
      cancel: () => {
        //Needs to be here, but empty
      }
    });
  }

  /**
   * Report opened callback
   * @param report
   */
  public reportOpened(report: any): void {
    this.setMode("report");
    this.reportOid = report.Oid;

    //*Multiple versions of a report in one valuation portfolio [#69438]
    // finalized or inactive reports (have new version) reports should be read-only
    const isReadOnly = (report.StatusId == 2 || report.RecordStatusId == 2);

    if (isReadOnly) {
      this.main.setEditMode(false);
      this.rdStorage.setAppWidgetHeaderMode(false);
      this.setToolbarReadOnly();
    }
    else {
      this.toolbarReadOnly = false;
      this.main.setEditMode(false);
      this.rdStorage.setAppWidgetHeaderMode(false);
      this.setEditMode(false);
    }
  }

  /**
   * Toggle the empty hashtags info
   */
  public toggleEmptyHashtagsNavigator(): void {
    if (this.rdStorage.getReport()) {
      this.rdStorage.setAppWidgetInfoMode(true);
      this.main.setInfoBarVisibility();
      this.eventBus.broadcastEvent("toggleEmptyHashtagsNavigator", {});
    }
  }

  /**
   * Set all actions to read-only
   */
  public setToolbarReadOnly(): void {
    this.toolbarReadOnly = true;
    this.main.setEditMode(false);
    this.rdStorage.setAppWidgetHeaderMode(false);
  }

  /**
   * Translate widgets action
   */
  public translateWidgets(): void {
    this.eventBus.broadcastEvent('rd:TranslateWidgets', true);
    this.router.navigate(['set']);
  }

  /**
   * Toggle edit mode
   */
  public toggleEditMode(): void {
    const editMode = this.rdStorage.getAppEditMode();
    this.setEditMode(!editMode);
  }

  /**
   * Set edit mode
   * @param mode
   */
  private setEditMode(mode: boolean): void {
    this.main.setEditMode(mode);
  }

  /**
   * Set header mode
   * @param mode
   */
  public setHeaderMode(mode: boolean): void {
    this.rdStorage.setAppWidgetHeaderMode(mode);
    this.setGridRasterMode(mode);
    if (mode) {
      this.main.setWidgetListVisibility(true);
    }
  }

  /**
   * Toggle header mode
   */
  public toggleHeaderMode(): void {
    const headerMode = this.rdStorage.getAppWidgetHeaderMode();
    this.setHeaderMode(!headerMode);
    const widgetsLockedByUser = this.widgetLocker.getLockedWidgetsForUser();
    if (!headerMode && widgetsLockedByUser?.length) {
      this.setEditMode(true);
    }
  }

  /**
   * Sets the grid raster mode
   * @param mode
   */
  public setGridRasterMode(mode: boolean): void {
    this.rdStorage.setAppReportGridRasterMode(mode);
  }

  /**
   * Toggles the grid raster mode
   */
  public toggleGridRasterMode(): void {
    const gridRasterMode = this.rdStorage.getAppReportGridRasterMode();
    this.setGridRasterMode(!gridRasterMode);
  }

  /**
   * Save template action
   */
  public saveDocument(): void {
    const activeDocument = this.rdStorage.getActiveDocument();
    const eventMessage = {
      templateData: activeDocument.data,
      templateLanguages: this.availableLanguages.map(o => o.id)
    };

    this.eventBus.broadcastEvent("saveTemplate", eventMessage);
  }

  /**
   * Load a template action
   */
  public loadTemplate(): void {
    const activeDocument = this.rdStorage.getActiveDocument();

    const eventMessage = {
      document: activeDocument,
      allReportDesignerLocales: this.appSettings.allReportDesignerLocales,
    }

    if (activeDocument?.data?.pages) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          this.main.setEditMode(false);
          this.rdStorage.setAppWidgetHeaderMode(false);
          this.rdStorage.setAppReportGridRasterMode(false);

          this.eventBus.broadcastEvent("loadTemplate", eventMessage);
        }, cancel: () => {
          //Nothing
        }
      }, 'confirmDialog');
    }
    else {
      this.eventBus.broadcastEvent("loadTemplate", eventMessage);
    }
  }

  /**
   * Save report action
   * @returns
   */
  public saveReport(): void {
    if (this.rdStorage.getReport()) {
      const activeDocument = this.rdStorage.getActiveDocument();

      if (activeDocument.data.isReadOnly) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            //Nothing
          }
        }, 'CANT_SAVE_INACTIVE_REPORT');
        return;
      }

      this.appLoadingIndicator.show();
      this.reportDesignerService.getDetails(this.rdStorage.getReport()).subscribe(reportResult => {
        const data = reportResult;
        data.TemplateJson = this.reportDesignerPersistanceService.getTemplateStringForSave(activeDocument.data);
        this.reportDesignerService.updateDetails(data).subscribe(() => {
          let hasWidgets = false;

          activeDocument.data.pages.forEach(page => {
            if (page.widgets.length) {
              hasWidgets = true;
            }
          })

          if (hasWidgets) {
            this.eventBus.broadcastEvent("saveChanges");
          } else {
            this.appLoadingIndicator.hide();
          }
        })
      });
    }
    else {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'NO_REPORT');
    }
  }

  /**
   * Delete template action
   */
  public deleteTemplate(): void {
    const activeDocument = this.rdStorage.getActiveDocument();

    const eventMessage = {
      document: activeDocument,
      allReportDesignerLocales: this.appSettings.allReportDesignerLocales,
    }

    if (activeDocument?.data?.pages) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          this.main.setEditMode(false);
          this.rdStorage.setAppWidgetHeaderMode(false);
          this.rdStorage.setAppReportGridRasterMode(false);

          this.eventBus.broadcastEvent("deleteTemplate", eventMessage);
        }, cancel: () => {
          //Nothing
        }
      }, 'confirmDialog');
    }
    else {
      this.eventBus.broadcastEvent("deleteTemplate", eventMessage);
    }
  }
}
